import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  VehiclesPage,
  VehicleDetailPage,
  DriversPage,
  OEMsPage,
  ModelsPage,
  HandoverListPage,
  HandoverDetailPage,
  RepoPage,
  MaintenancePage,
  IncidentsPage,
  IncidentDetailPage,
  MonitoringPage,
  MonitoringDetailPage,
  NotFoundPage,
  DashboardPage,
  PreReturnHandoverDetailPage,
  SettingsPage,
  RentEscalationSettingPage,
  AlertAssigneesPage,
  NotificationChannelsPage,
  RentTrackingPage,
  InvoicePage,
  StnkManagementPage,
  PurchaseOrderListPage,
  PurchaseOrderDetailPage,
  PackageListPage,
  CreatePackagePage,
  RentDashboardPage,
  RentPaymentsPage,
  RentActivityPage,
  RentalDetailPage,
  OutstandingListPage,
  OutstandingDetailPage,
} from '@/pages';
import RepoDetailPage from '@/pages/repossessions/RepoDetailPage';
import MaintenanceDetailPage from '@/pages/maintenance/MaintenanceDetailPage';
import { RouteConfig, RouteMeta } from '@/types';
import { buildRouteMeta, getRouteTitle } from './routeMeta';

export const DASHBOARD_ROUTE: RouteConfig = {
  path: '/dashboard',
  element: <DashboardPage />,
  title: 'Dashboard',
  icon: 'dashboard',
};

export const HOME_ROUTE: RouteConfig = {
  path: '/home',
  element: (
    <div className="justify-center align-middle text-center pt-[calc(100vh/2)] sm:text-2xl font-bold text-slate-900 mb-3 tracking-tight">
      Something awesome is coming.
    </div>
  ),
  title: 'Home',
  mobileTitle: 'Beranda',
  icon: 'home',
};

export const routes: RouteConfig[] = [
  {
    path: '/vehicles',
    element: <VehiclesPage />,
    title: 'Vehicles',
    mobileTitle: 'Kendaraan',
    icon: 'directions_car',
  },
  {
    path: '/vehicles/:id',
    element: <VehicleDetailPage />,
    title: 'Vehicle Detail',
    mobileTitle: 'Kendaraan',
  },
  {
    path: '/units',
    element: <VehiclesPage />,
    title: 'Units',
    mobileTitle: 'Unit',
    icon: 'inventory_2',
  },
  {
    path: '/drivers',
    element: <DriversPage />,
    title: 'Drivers',
    mobileTitle: 'Mitra',
    icon: 'person',
  },
  {
    path: '/handovers',
    element: <HandoverListPage />,
    title: 'Handovers',
    mobileTitle: 'Handover',
    icon: 'swap_horiz',
  },
  {
    path: '/handovers/:id',
    element: <HandoverDetailPage />,
    title: 'Handover Detail',
    mobileTitle: 'Handover',
  },
  {
    path: '/handover/:id',
    element: <HandoverDetailPage />,
    title: 'Handover Detail',
    mobileTitle: 'Handover',
    parentPath: '/handovers',
  },
  {
    path: '/handover/pre-return/:id',
    element: <PreReturnHandoverDetailPage />,
    title: 'Pre-Return Handover Detail',
    mobileTitle: 'Handover',
    parentPath: '/handovers',
  },
  {
    path: '/models',
    element: <ModelsPage />,
    title: 'Models',
    mobileTitle: 'Model',
    icon: 'ri-motorbike-line',
  },
  {
    path: '/oems',
    element: <OEMsPage />,
    title: 'OEMs',
    mobileTitle: 'OEM',
    icon: 'ri-building-2-line',
  },
  {
    path: '/repossessions',
    element: <RepoPage />,
    title: 'Repossessions',
    mobileTitle: 'Repossession',
    icon: 'ri-shield-line',
  },
  {
    path: '/repossessions/:slug',
    element: <RepoDetailPage />,
    title: 'Repo Detail',
    mobileTitle: 'Repossession',
  },
  {
    path: '/maintenances',
    element: <MaintenancePage />,
    title: 'Maintenances',
    mobileTitle: 'Maintenance',
    icon: 'ri-settings-5-line',
  },
  {
    path: '/maintenances/:slug',
    element: <MaintenanceDetailPage />,
    title: 'Maintenance Detail',
    mobileTitle: 'Maintenance',
  },
  {
    path: '/purchase-orders',
    element: <PurchaseOrderListPage />,
    title: 'Purchase Order',
    icon: 'ri-shopping-cart-2-line',
  },
  {
    path: '/purchase-orders/:id',
    element: <PurchaseOrderDetailPage />,
    title: 'Purchase Order Detail',
    mobileTitle: 'Purchase Order',
  },
  {
    path: '/packages',
    element: <PackageListPage />,
    title: 'Package',
    mobileTitle: 'Paket',
    icon: 'ri-vip-diamond-line',
  },
  {
    path: '/packages/create',
    element: <CreatePackagePage />,
    title: 'Create Package',
    mobileTitle: 'Paket',
    parentPath: '/packages',
  },
  {
    path: '/packages/:id',
    element: <CreatePackagePage />,
    title: 'Detail Package',
    mobileTitle: 'Paket',
  },
  {
    path: '/packages/:id/edit',
    element: <CreatePackagePage />,
    title: 'Edit Package',
    mobileTitle: 'Paket',
    parentPath: '/packages',
  },
  {
    path: '/rent-dashboard',
    element: <RentDashboardPage />,
    title: 'Rent Dashboard',
    mobileTitle: 'Dashboard Rental',
    icon: 'ri-dashboard-2-line',
  },
  {
    path: '/rent-payments',
    element: <RentPaymentsPage />,
    title: 'Pembayaran',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    path: '/rent-activity',
    element: <RentActivityPage />,
    title: 'Rent Activity',
    mobileTitle: 'Aktivitas Sewa',
    icon: 'ri-article-line',
  },
  {
    path: '/rent-activity/:id',
    element: <RentalDetailPage />,
    title: 'Detail Rent',
    mobileTitle: 'Aktivitas Sewa',
  },
  {
    path: '/rent-tracking',
    element: <RentTrackingPage />,
    title: 'Tracking',
    icon: 'ri-map-pin-line',
  },
  {
    path: '/outstandings',
    element: <OutstandingListPage />,
    title: 'Outstanding',
    icon: 'ri-hand-coin-line',
  },
  {
    path: '/outstandings/:id',
    element: <OutstandingDetailPage />,
    title: 'Detail Outstanding',
    mobileTitle: 'Outstanding',
  },
  // {
  //   path: '/issues',
  //   element: <IssuesPage />,
  //   title: 'Issues',
  //   icon: 'ri-error-warning-line',
  // },
  {
    path: '/incidents',
    element: <IncidentsPage />,
    title: 'Incidents',
    mobileTitle: 'Insiden',
    icon: 'ri-alarm-warning-line',
  },
  {
    path: '/incidents/:slug',
    element: <IncidentDetailPage />,
    title: 'Incident Detail',
    mobileTitle: 'Insiden',
  },
  {
    path: '/2w/invoice',
    element: <InvoicePage />,
    title: 'Invoice',
    icon: 'ri-file-list-3-line',
  },
  {
    path: '/monitoring',
    element: <MonitoringPage />,
    title: 'Monitoring',
    icon: 'ri-pulse-line',
  },
  {
    path: '/2w/stnk',
    element: <StnkManagementPage />,
    title: 'STNK Management',
    mobileTitle: 'STNK',
    icon: 'ri-file-shield-2-line',
  },
  {
    path: '/monitoring/:id',
    element: <MonitoringDetailPage />,
    title: 'Alert Detail',
    mobileTitle: 'Monitoring',
  },
  {
    path: '/4w/home',
    element: (
      <div className="justify-center align-middle text-center pt-[calc(100vh/2)] sm:text-2xl font-bold text-slate-900 mb-3 tracking-tight">
        Something awesome is coming.
      </div>
    ),
    title: 'Home',
    mobileTitle: 'Beranda',
    icon: 'home',
  },

  {
    path: '/settings',
    element: <SettingsPage />,
    title: 'Settings',
    mobileTitle: 'Pengaturan',
    icon: 'settings',
  },
  {
    path: '/settings/rent-escalation',
    element: <RentEscalationSettingPage />,
    title: 'Settings — Eskalasi Rent',
    mobileTitle: 'Pengaturan',
  },
  {
    path: '/settings/alert-assignees',
    element: <AlertAssigneesPage />,
    title: 'Settings — Slack Mention',
    mobileTitle: 'Pengaturan',
  },
  {
    path: '/settings/notification-channels',
    element: <NotificationChannelsPage />,
    title: 'Settings — Slack Channel',
    mobileTitle: 'Pengaturan',
  },
  {
    path: '/support',
    element: (
      <div className="p-6">
        <h1>Support - Coming Soon</h1>
      </div>
    ),
    title: 'Support',
    mobileTitle: 'Bantuan',
    icon: 'help',
  },

  {
    path: '/',
    element: <Navigate to="/oems" replace />,
    title: 'Home',
    mobileTitle: 'Beranda',
  },

  {
    path: '*',
    element: <NotFoundPage />,
    title: 'Page Not Found',
    mobileTitle: 'Halaman Tidak Ditemukan',
  },
];

/**
 * Document title for the current pathname. Uses the same pattern-aware lookup
 * as `getRouteMeta`, so `/vehicles/123` resolves to `/vehicles/:id`; unknown
 * paths fall back to `DEFAULT_TITLE`.
 */
export const getPageTitle = (pathname: string): string =>
  getRouteTitle([DASHBOARD_ROUTE, HOME_ROUTE, ...routes], pathname);

/**
 * Header metadata for the current pathname (`Layout` below `md`). Uses
 * react-router's `matchPath`, so `/vehicles/123` resolves to `/vehicles/:id`.
 * Feature-flagged routes (`DASHBOARD_ROUTE`, `HOME_ROUTE`) are included since
 * their titles are static even when the page itself is gated.
 */
export const getRouteMeta = (pathname: string): RouteMeta =>
  buildRouteMeta([DASHBOARD_ROUTE, HOME_ROUTE, ...routes], pathname);
